import Image from "next/image";
import React from "react";
import useUser from "../../hooks/useUser";
import { Menu, Transition } from "@headlessui/react";

import Button from "../Micro/Button";
import { useRouter } from "next/router";
import useLogout from "../../hooks/useLogout";
import useTheme from "../../hooks/useTheme";
import config from "../../config";
import {
  ChevronDown,
  UserCircle,
  ExternalLink,
  Plus,
  Moon,
  Sun,
  LogOut,
} from "lucide-react";
import { MemberType } from "@/types/team.type";
export type UserMenuType = {
  username?: string;
  link?: string;
  teams: any[];
  type?: "team" | "user";
  avatar: string;
};

const UserMenu = ({ username, teams, type, avatar }: UserMenuType) => {
  const router = useRouter();
  const logout = useLogout();
  const { user } = useUser();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="z-[999] flex flex-row items-center space-x-2 border-none">
      <Menu as="div" className="relative inline-block border-none text-left">
        <div className="min-w-fit">
          <Menu.Button className="flex cursor-pointer items-center justify-between rounded-3xl border-none bg-white px-3 py-2 text-sm font-medium text-gray-800 transition-all hover:bg-gray-50 dark:bg-theme-color dark:text-gray-200 dark:hover:bg-gray-700">
            <div className="flex items-center space-x-2">
              <Image
                src={avatar}
                width={28}
                height={28}
                className="rounded-full"
                alt="Avatar"
                priority
              />
              <span className="text-sm font-medium capitalize">{username}</span>
            </div>
            <ChevronDown className="ml-2 h-4 w-4 text-gray-500" />
          </Menu.Button>
        </div>

        <Transition
          as={React.Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right divide-y divide-dashed divide-gray-100 rounded-none border-none bg-white shadow-md focus:outline-none dark:divide-gray-700 dark:bg-theme-color-light dark:ring-gray-800">
            {/* User/Team Section */}
            <div className="border-none p-3">
              {type === "team" && (
                <Menu.Item as={React.Fragment}>
                  {() => (
                    <div
                      className="mb-2 block cursor-pointer"
                      onClick={() => router.push(`/teams/${username}/projects`)}
                    >
                      <div className="flex items-center space-x-3 rounded-none bg-blue-50 p-2 transition-colors dark:bg-blue-900/20">
                        <Image
                          src={avatar}
                          width={32}
                          height={32}
                          className="rounded-full"
                          alt="Team Avatar"
                          priority
                        />
                        <div>
                          <p className="text-sm font-semibold capitalize text-gray-900 dark:text-gray-100">
                            {username}
                          </p>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Team Account
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </Menu.Item>
              )}

              <Menu.Item as={React.Fragment}>
                {() => (
                  <div
                    onClick={() => {
                      router.push("/overview");
                    }}
                    className="block cursor-pointer"
                  >
                    <div
                      className={`flex items-center space-x-3 rounded-none p-2 transition-colors ${
                        type === "user"
                          ? "bg-light dark:bg-blue-900/20"
                          : "hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      <div className="relative">
                        <Image
                          src={
                            user?.avatar ||
                            `${config.AVATAR_URL}/adventurer-neutral/svg?seed=${
                              user?.username || user?.first_name
                            }`
                          }
                          width={32}
                          height={32}
                          className="rounded-full"
                          priority
                        />
                        <UserCircle className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-white text-blue-600 dark:bg-transparent dark:text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold capitalize text-gray-900 dark:text-gray-100">
                          {user?.first_name} {user?.last_name}
                        </p>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Personal Account
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </Menu.Item>
            </div>

            {/* Teams Section */}
            {teams?.length > 0 && (
              <div className="p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Teams
                  </span>
                  <div
                    onClick={() => router.push("/teams")}
                    className="flex items-center text-xs text-blue-600 hover:underline dark:text-blue-400"
                  >
                    <span>View all</span>
                    <ExternalLink className="ml-1 h-3 w-3" />
                  </div>
                </div>

                <div className="space-y-1">
                  {teams
                    .filter((team) => team.name !== username && team.accepted)
                    .slice(0, type === "team" ? 2 : 3)
                    .map((team) => (
                      <Menu.Item key={team.name} as={React.Fragment}>
                        {({ active }) => (
                          <div
                            onClick={() => router.push(`/teams/${team.name}`)}
                            className="block cursor-pointer"
                          >
                            <div
                              className={`flex items-center space-x-3 p-2 transition-colors ${
                                active ? "bg-gray-100 dark:bg-gray-700" : ""
                              }`}
                            >
                              <div className="relative">
                                <Image
                                  src={
                                    team?.avatar ||
                                    `${config.AVATAR_URL}/initials/svg?seed=${team?.name}`
                                  }
                                  width={32}
                                  height={32}
                                  className="rounded-full"
                                  alt="Team Avatar"
                                  priority
                                />
                                {team.isCreator && (
                                  <div className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-white">
                                    <svg
                                      width="8"
                                      height="8"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                                        fill="currentColor"
                                      />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-semibold capitalize text-gray-900 dark:text-gray-100">
                                  {team.name}
                                </p>
                                <span className="text-xs capitalize text-gray-500 dark:text-gray-400">
                                  {team?.members
                                    ?.find(
                                      (member: MemberType) =>
                                        member?.email === user?.email,
                                    )
                                    ?.role.toLowerCase()}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </Menu.Item>
                    ))}
                </div>

                <Button
                  onClick={() => router.push("/teams/new")}
                  className="mt-2 flex w-full items-center justify-center rounded-md border border-blue-200 bg-transparent px-3 py-2 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  <span>Create Team</span>
                </Button>
              </div>
            )}

            {/* Actions Section */}
            <div className="space-y-1 p-3">
              <Menu.Item as="div">
                <button
                  onClick={toggleTheme}
                  className="flex w-full items-center rounded-md p-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {theme === "light" ? (
                    <>
                      <Moon className="mr-2 h-4 w-4" />
                      <span>Dark Mode</span>
                    </>
                  ) : (
                    <>
                      <Sun className="mr-2 h-4 w-4" />
                      <span className="text-white">Light Mode</span>
                    </>
                  )}
                </button>
              </Menu.Item>

              <Menu.Item as="div">
                <button
                  onClick={() => logout()}
                  className="flex w-full items-center rounded-md p-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Logout</span>
                </button>
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};

export default UserMenu;
